import {
  ColumnContext,
  ColumnAxis,
  expandDivisionsByKey,
  useColumnDivisions,
} from "@macrostrat/column-components";
import { useContext, useRef, useState, useLayoutEffect } from "react";
import h from "./main.module.styl";
import { format } from "d3-format";
import { GeneralizedDivision } from "./types";

const fmt = format("i");

const GeneralizedAxis = function (props) {
  const { height, zoom, scale, pixelsPerMeter } = useContext(ColumnContext);
  const ratio = pixelsPerMeter * zoom;

  // Keep labels from inhabiting the top few pixels (to make space for section labels)
  const topPadding = 30;
  const maxVal = scale.domain()[1] - topPadding / ratio;

  return h(ColumnAxis, {
    ticks: height * zoom,
    tickSize: 4,
    showLabel(d) {
      return false;
    },
  });
};

interface SectionBreakProps {
  division: GeneralizedDivision;
  textPadding?: number;
}

const SectionBreak = (props: SectionBreakProps) => {
  const { scale } = useContext(ColumnContext);
  const { division: d, extraHeight = 0 } = props;
  const btm = scale(d.bottom);
  const top = scale(d.top);
  const height = btm - top + extraHeight;
  const heightM = d.top - d.bottom;
  const textPadding = props.textPadding ?? 10;

  const ref = useRef<HTMLElement>();
  const [rect, setRect] = useState<DOMRect>(null);

  useLayoutEffect(() => {
    if (ref.current == null) return;
    const rect = ref.current.getBoundingClientRect();
    setRect(rect);
  }, [ref]);

  const textHeight = rect?.height ?? 0;
  const textWidth = rect?.width ?? 0;
  const showPrefix = textHeight < height - 2 * textPadding && textWidth < 30;

  return h(
    "div.section-break",
    {
      style: { height },
      className: `section-${d.original_section}`,
    },
    [
      h("span", { ref }, [
        h("span.section-break-title", [
          h.if(showPrefix)("span.prefix", "Section "),
          h("span.section-id", `${d.original_section}`),
        ]),
        h("span.height", `${Math.round(heightM)} m`),
      ]),
    ],
  );
};

const GeneralizedBreaks = (props) => {
  const divisions = useColumnDivisions();

  let breaks: GeneralizedDivision[] = expandDivisionsByKey(
    divisions.filter((d) => d.original_section != null),
    "original_section",
  );
  breaks.reverse();

  return h(
    "div.generalized-breaks",
    breaks.map((division, i) =>
      h(SectionBreak, {
        division,
        // This seems to be necessary to properly align the section ... not sure why
        extraHeight: i == breaks.length - 1 ? 1.5 : 0,
      }),
    ),
  );
};

export { GeneralizedAxis, GeneralizedBreaks };
